import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraFormField, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useId } from 'react';
import { z } from 'zod';
const contextShape = z.strictObject({
    schema: z.literal('program-context-v1'),
    summary: z.string(),
    capabilities: z.array(z.string()).default([]),
    startDate: z.string().default(''),
    endDate: z.string().default(''),
    contractId: z.string().default('')
});
const date = z.union([
    z.literal(''),
    z.iso.date()
]);
const publication = contextShape.extend({
    summary: z.string().trim().min(1).max(8000),
    capabilities: z.array(z.string().trim().min(1).max(255)).max(30),
    startDate: date,
    endDate: date,
    contractId: z.union([
        z.literal(''),
        z.uuid()
    ])
}).refine((v)=>!v.startDate || !v.endDate || v.startDate <= v.endDate);
export function readProgramContext(content) {
    try {
        const result = contextShape.safeParse(JSON.parse(content));
        return result.success ? result.data : null;
    } catch  {
        return null;
    }
}
export function validProgramContext(content) {
    const parsed = publication.safeParse(readProgramContext(content));
    if (!parsed.success) return false;
    const bytes = (value)=>new TextEncoder().encode(value).length;
    return bytes(content) <= 32768 && bytes(parsed.data.summary) <= 8000 && parsed.data.capabilities.every((capability)=>bytes(capability) <= 255);
}
export const emptyProgramContext = JSON.stringify({
    schema: 'program-context-v1',
    summary: '',
    capabilities: [],
    startDate: '',
    endDate: '',
    contractId: ''
});
// Only explicitly authored permitted fields enter the frozen Library release.
// Never prefill from Program descriptions, working prose or operational rows.
export function ProgramContextEditor({ content, onChange, disabled }) {
    const id = useId();
    const value = readProgramContext(content);
    if (!value) return null;
    const update = (key, next)=>onChange(JSON.stringify({
            ...value,
            [key]: next
        }));
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "aria-label": "Program context publication",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Review only reusable summary, capabilities and dates for the selected audience. Do not include finances, employee information, deliverables or private notes. Saving keeps a draft; Release publishes it separately."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Permitted Program summary",
                htmlFor: `${id}-summary`,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "data-testid": Testids.programContext.summary,
                    id: `${id}-summary`,
                    value: value.summary,
                    disabled: disabled,
                    maxLength: 8000,
                    onChange: (e)=>update('summary', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Capabilities (one per line)",
                htmlFor: `${id}-capabilities`,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "data-testid": Testids.programContext.capabilities,
                    id: `${id}-capabilities`,
                    value: value.capabilities.join('\n'),
                    disabled: disabled,
                    onChange: (e)=>update('capabilities', e.target.value ? e.target.value.split('\n') : [])
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Program start date",
                htmlFor: `${id}-start`,
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "data-testid": Testids.programContext.startDate,
                    id: `${id}-start`,
                    type: "date",
                    value: value.startDate,
                    disabled: disabled,
                    onChange: (e)=>update('startDate', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Program end date",
                htmlFor: `${id}-end`,
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "data-testid": Testids.programContext.endDate,
                    id: `${id}-end`,
                    type: "date",
                    value: value.endDate,
                    disabled: disabled,
                    onChange: (e)=>update('endDate', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "An end date is not a confirmed recompete date."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Canonical Registry contract ID (optional)",
                htmlFor: `${id}-contract`,
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "data-testid": Testids.programContext.contractId,
                    id: `${id}-contract`,
                    value: value.contractId,
                    disabled: disabled,
                    onChange: (e)=>update('contractId', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Use the contract ID from Registry. Readers need separate Registry permission to see this link. Blank dates and capabilities mean not supplied."
            }),
            !validProgramContext(content) && /*#__PURE__*/ _jsx("output", {
                children: "Supply a summary, up to 30 nonempty capabilities, valid ordered dates and an optional Registry contract UUID before saving."
            })
        ]
    });
}
